import React, { useEffect, useState } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import BackendService from '../../../services/admin/BackendService';
import { InvestorAllocationsResponse } from '../../../models/InvestmentEntity';
import { Permissions } from '../../../models/admin/Permission';
import { usePermissions } from 'PermissionContext';
import MessageToast from '../../MessageToast';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading } from '../../styles/styles';
import StatementAllocationWarnings from './StatementAllocationWarnings';
import StatementBatchStatus from './StatementBatchStatus';
import { useInvestorStatementGeneration } from './useInvestorStatementGeneration';

interface StatementsSectionProps {
  investorExternalId: string;
  /** Fires when a statement is confirmed — confirming adds it to the investor's document library. */
  onLibraryChanged?: () => void;
}

type ToastType = 'error' | 'warning' | 'info' | 'success';

const StatementsSection: React.FC<StatementsSectionProps> = ({
  investorExternalId,
  onLibraryChanged,
}) => {
  const { hasPermission } = usePermissions();
  const canGenerate = hasPermission(Permissions.InvestorStatements);
  const { batch, generating, error, generate, applyItems } = useInvestorStatementGeneration(
    investorExternalId,
    onLibraryChanged,
  );
  const [allocations, setAllocations] = useState<InvestorAllocationsResponse | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const showToast = (message: string, type: ToastType) => setToast({ message, type });

  useEffect(() => {
    if (!canGenerate) return;
    new BackendService()
      .getInvestorAllocations(investorExternalId, false)
      .then(setAllocations)
      .catch(() => setAllocations(null));
  }, [investorExternalId, canGenerate]);

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  if (!canGenerate) return null;

  const hasNoEntities = !!allocations && allocations.allocations.length === 0;
  const isUnderAllocated = !!allocations && !hasNoEntities && allocations.remainingAllocation > 0;

  return (
    <StyledSection tone="tone2">
      <StyledHeading>Investor statements</StyledHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generates one statement per Investment Entity this investor is allocated to, dated today.
        Statements are staged for review — nothing is visible to the investor until you confirm each
        one. Generating again always replaces what's there; you'll be warned before anything is
        overwritten.
      </Typography>

      <StatementAllocationWarnings
        allocations={allocations}
        hasNoEntities={hasNoEntities}
        isUnderAllocated={isUnderAllocated}
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Button
            variant="contained"
            disabled={generating || hasNoEntities}
            onClick={() => void generate()}
          >
            {generating ? 'Generating…' : 'Generate Statement'}
          </Button>
        </Stack>
      </Paper>

      {batch && (
        <StatementBatchStatus batch={batch} onItemsChange={applyItems} onShowToast={showToast} />
      )}

      <MessageToast
        type={toast?.type ?? 'info'}
        message={toast?.message ?? ''}
        open={!!toast}
        onClose={() => setToast(null)}
      />
    </StyledSection>
  );
};

export default StatementsSection;
